import React, { useEffect } from "react";
import PropTypes from "prop-types";
import PopUpOutline from "../PopUpOutline";
import ImageButton from "../ImageButton";
import { useTranslation } from "react-i18next";
import { getPurchaseTransactionType, getOnDemand4KAvailability } from "../../shared/utils";
import { getFormattedPrice, getPackagePrice } from "../../shared/utils/feedHelper";
import useAppLanguage from "../../shared/hooks/useAppLanguage";
import constants from "../../shared/constants";

import "./style.scss";
import { logDTEvent } from "../../shared/analytics/dynatrace";
import { DT_EVENTS } from "../../shared/constants/dynatrace";
import { useReducers } from "../../shared/hooks/useReducer";

const {
  TRANSACTION_TYPES,
  DEFAULT_RENTAL_DURATION,
  ORDER_OPTIONS_TOOLTIP_VERTICAL_OFFSET,
  ORDER_OPTIONS_TOOLTIP_HORIZONTAL_OFFSET,
  REDUCER_TYPE,
} = constants;

const INFO_ICON = process.env.PUBLIC_URL + "/images/info-icon-orange.svg";

/**
 * Ways to order prompt component
 * @component
 * @param {Object} props
 */
const WaysToOrderPrompt = ({
  orderOptions = [],
  onClosePrompt = () => {},
  onOptionClick = () => {},
  onShowTooltip = () => {},
}) => {
  const { t: translate } = useTranslation();
  const { appLanguage } = useAppLanguage();
  const { featureToggles } = useReducers(REDUCER_TYPE.APP);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "visible";
      onShowTooltip({ visible: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    onShowTooltip({
      visible: true,
      position: {
        top: rect.bottom + window.scrollY - ORDER_OPTIONS_TOOLTIP_VERTICAL_OFFSET,
        left: rect.left + window.scrollX - ORDER_OPTIONS_TOOLTIP_HORIZONTAL_OFFSET,
      },
    });
  };

  const handleMouseLeave = () => {
    if (onShowTooltip) {
      onShowTooltip({ visible: false });
    }
  };

  const filteredOrderOptions = [
    orderOptions.find((option) => getPurchaseTransactionType(option.name) === TRANSACTION_TYPES.RENT),
    orderOptions.find((option) => getPurchaseTransactionType(option.name) === TRANSACTION_TYPES.BUY),
  ].filter(Boolean);

  const hasOnly4K = getOnDemand4KAvailability(orderOptions);

  return (
    <>
      <div className="ways-to-order-backdrop"></div>
      <div className="ways-to-order-prompt-container">
        <div className="ways-to-order-container">
          <PopUpOutline className="ways-to-order">
            <div className="ways-to-order-label-container">
              <div className="ways-to-order-label">{translate("order_options")}</div>
              <ImageButton
                src={process.env.PUBLIC_URL + "/images/playback-close.svg"}
                alt={translate("close")}
                onClickHandler={() => {
                  logDTEvent(DT_EVENTS.PURCHASE_CANCELLED, null, featureToggles.isDynatraceTrackingEnabled);
                  onClosePrompt();
                }}
              />
            </div>
            {filteredOrderOptions.map((purchasePackage) => {
              const transactionType = getPurchaseTransactionType(purchasePackage.name);
              const isRentTransaction = transactionType === TRANSACTION_TYPES.RENT;
              const isOnly4K = isRentTransaction ? hasOnly4K.rentOnly4K : hasOnly4K.buyOnly4K;
              const price = getPackagePrice(purchasePackage) ?? 0;
              return (
                <div
                  key={purchasePackage.id}
                  className="order-option-wrap"
                  onClick={() => onOptionClick(purchasePackage)}
                >
                  <div className="details-wrapper">
                    <div className="order-option-action">
                      {translate(isRentTransaction ? "rent_for" : "buy_for")} {getFormattedPrice(price, appLanguage)}
                    </div>
                    <div className="order-option-description">
                      {isRentTransaction
                        ? translate("order_available_hours").replace("<duration>", DEFAULT_RENTAL_DURATION)
                        : translate("order_title")}
                      {isOnly4K ? (
                        <div className="ways-to-order-warning-message">
                          {translate("4k_only_warning")}
                          <span
                            className="ways-to-order-icon-wrapper"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          >
                            <img src={INFO_ICON} alt="Info Icon" />
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="action-wrapper">
                    <ImageButton
                      src={process.env.PUBLIC_URL + "/images/order.svg"}
                      alt={translate(transactionType)}
                      testID="orderIcon"
                    />
                  </div>
                </div>
              );
            })}
          </PopUpOutline>
        </div>
      </div>
    </>
  );
};

WaysToOrderPrompt.propTypes = {
  orderOptions: PropTypes.array.isRequired,
  onClosePrompt: PropTypes.func.isRequired,
  onOptionClick: PropTypes.func.isRequired,
};

export default WaysToOrderPrompt;
